import moment from 'moment';
import { generateEventId, parseEventId } from './utils';

// --- Day fill ----------------------------------------------------------------
// "Schedule a 4h block of Jerry on Monday, grab it and drag it across Tuesday,
// Wednesday, ... and it fills in for those days too." The pure part of that
// feature: given the source block and how many columns the handle was dragged,
// decide which copies to add and which days to leave alone. The gesture itself
// lives in DayFillDrag.tsx.

/**
 * A series/paid-programming block on the calendar. Only id/start/end matter
 * here; every other field is carried over to the copies untouched.
 */
export type DayFillEvent = {
    id: string;
    start: Date;
    end: Date;
    title?: string;
    [key: string]: any;
};

export type DayFillDay = {
    /** Days from the source block's day. Never 0. */
    offset: number;
    /** Start of the target day. */
    date: Date;
    /** The copy to add, or null when the day is blocked. */
    event: DayFillEvent | null;
    /** Title of the block already on that day that the copy would overlap. */
    blockedBy: string | null;
    /** The blocking block is this same series/media: the day is already filled. */
    alreadyThere: boolean;
};

export type DayFillPlan = {
    days: DayFillDay[];
    added: DayFillEvent[];
    skipped: DayFillDay[];
};

/**
 * Day offsets from the source column to the target column, nearest first:
 * (0, 3) -> [1, 2, 3], (3, 1) -> [-1, -2]. Empty when they are the same column.
 */
export const dayFillOffsets = (sourceIndex: number, targetIndex: number): number[] => {
    const step = targetIndex >= sourceIndex ? 1 : -1;
    const offsets: number[] = [];
    for (let i = sourceIndex + step; step > 0 ? i <= targetIndex : i >= targetIndex; i += step) {
        offsets.push(i - sourceIndex);
    }
    return offsets;
};

const overlaps = (a: DayFillEvent, b: DayFillEvent): boolean =>
    a.start.getTime() < b.end.getTime() && a.end.getTime() > b.start.getTime();

const eventTitle = (event: DayFillEvent): string => event.title || parseEventId(event.id).objId;

/**
 * Copies of `source` on each offset day at the same wall-clock time. A day is
 * skipped (not overwritten) when any existing block overlaps the copy, which
 * includes an identical block already being there. Days are added with moment
 * so the time of day survives a DST change.
 */
export const planDayFill = (
    source: DayFillEvent,
    offsets: number[],
    existing: DayFillEvent[]
): DayFillPlan => {
    const { objId } = parseEventId(source.id);
    const days: DayFillDay[] = offsets
        .filter((offset) => offset !== 0)
        .map((offset) => {
            const start = moment(source.start).add(offset, 'days').toDate();
            const end = moment(source.end).add(offset, 'days').toDate();
            const copy: DayFillEvent = { ...source, id: generateEventId(objId, start, end), start, end };
            const blocker = existing.find((event) => overlaps(event, copy));
            return {
                offset,
                date: moment(start).startOf('day').toDate(),
                event: blocker ? null : copy,
                blockedBy: blocker ? eventTitle(blocker) : null,
                alreadyThere: !!blocker && parseEventId(blocker.id).objId === objId,
            };
        });
    return {
        days,
        added: days.flatMap((day) => (day.event ? [day.event] : [])),
        skipped: days.filter((day) => !day.event),
    };
};

const formatDay = (date: Date): string => moment(date).format('ddd D MMM');

const joinList = (items: string[]): string => {
    if (items.length <= 1) return items.join('');
    return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
};

/**
 * The toasts shown after a fill: what was added (success), days that already
 * had this block so nothing was needed (info), and days left alone because a
 * different block is in the way (warning).
 */
export const describeDayFill = (
    source: DayFillEvent,
    plan: DayFillPlan
): { success: string | null; info: string | null; warning: string | null } => {
    const title = eventTitle(source);
    const alreadyThere = plan.skipped.filter((day) => day.alreadyThere);
    const clashing = plan.skipped.filter((day) => !day.alreadyThere);
    const success = plan.added.length
        ? `Repeated "${title}" on ${joinList(plan.added.map((event) => formatDay(event.start)))}.`
        : null;
    const info = alreadyThere.length
        ? `${joinList(alreadyThere.map((day) => formatDay(day.date)))} already ${alreadyThere.length === 1 ? 'has' : 'have'} "${title}" at that time.`
        : null;
    const warning = clashing.length
        ? `Skipped ${joinList(clashing.map((day) => `${formatDay(day.date)} (overlaps "${day.blockedBy}")`))}.`
        : null;
    return { success, info, warning };
};
